import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    if (this.hasAttribute('data-variant-media-sync')) {
      this.addEventListener('click', this.#handleVariantMediaClick, { signal });
    }
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // KLAR Flasken supplies its color-specific media through
    // klar-variant-media. Keep the initialized slideshow instance in place so
    // its controls remain connected after a color change.
    if (this.hasAttribute('data-klar-variant-gallery')) return;

    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Selects the variant whose featured media was clicked.
   * The normal picker change event keeps price, form, selling plan and gallery in sync.
   *
   * @param {MouseEvent} event
   */
  #handleVariantMediaClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const mediaElement = event.target.closest('[data-media-id]');
    const mediaId = mediaElement?.getAttribute('data-media-id');
    if (!mediaId) return;

    const mapElement = this.querySelector('script[data-variant-media-map]');
    if (!mapElement?.textContent) return;

    let variantByMedia;
    try {
      variantByMedia = JSON.parse(mapElement.textContent);
    } catch {
      return;
    }

    const variantId = variantByMedia[mediaId];
    if (!variantId) return;

    const section = this.closest('.shopify-section');
    const variantInput = section?.querySelector(`variant-picker input[data-variant-id="${variantId}"]`);
    if (!(variantInput instanceof HTMLInputElement) || variantInput.checked) return;

    variantInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
